import posthog from "posthog-js";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://us.i.posthog.com";

export function initPostHog() {
  if (!POSTHOG_KEY) {
    console.warn("[PostHog] VITE_POSTHOG_KEY not configured. Analytics disabled.");
    return;
  }
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: ".sensitive",
      // Record all sessions — helps replay mobile scroll issues visually
      recordCrossOriginIframes: false,
    },
    enable_recording_console_log: true,
    disable_session_recording: false,
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
  });
  console.log("[PostHog] Initialized");

  // Track scroll depth on every page — detects when users can't scroll
  initScrollDepthTracking();
}

/**
 * Scroll Depth Tracking
 * Fires a PostHog event when the user scrolls to 25%, 50%, 75%, 100% of a page.
 * If no scroll events fire (stuck at 0%), it means the user could NOT scroll — 
 * which helps detect mobile scroll bugs like the DashboardLayout issue.
 */
function initScrollDepthTracking() {
  if (!POSTHOG_KEY) return;

  let maxScrollPercent = 0;
  const milestones = [25, 50, 75, 100];
  const reached = new Set<number>();

  const getScrollPercent = (): number => {
    // Try the main scrollable container first (DashboardLayout uses overflow-y-auto divs)
    const scrollContainers = Array.from(document.querySelectorAll<HTMLElement>(
      ".overflow-y-auto, [data-scroll-container]"
    ));

    for (const el of scrollContainers) {
      if (el.scrollHeight > el.clientHeight + 10) {
        const pct = Math.round((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100);
        if (pct > 0) return pct;
      }
    }

    // Fallback to window scroll
    const winH = document.documentElement.scrollHeight - window.innerHeight;
    if (winH <= 0) return 0;
    return Math.round((window.scrollY / winH) * 100);
  };

  const onScroll = () => {
    const pct = getScrollPercent();
    if (pct > maxScrollPercent) {
      maxScrollPercent = pct;
      for (const milestone of milestones) {
        if (pct >= milestone && !reached.has(milestone)) {
          reached.add(milestone);
          posthog.capture("scroll_depth", {
            percent: milestone,
            page: window.location.pathname,
            is_mobile: window.innerWidth < 768,
            device_width: window.innerWidth,
          });
        }
      }
    }
  };

  // Listen on window AND all overflow-y-auto containers
  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("scroll", onScroll, { passive: true, capture: true });

  // Reset tracking on page navigation (SPA route changes)
  const originalPushState = history.pushState.bind(history);
  history.pushState = (...args) => {
    originalPushState(...args);
    maxScrollPercent = 0;
    reached.clear();
    // Report page_no_scroll if user left page without scrolling at all
    // (only if page was tall enough to scroll)
    setTimeout(() => {
      const containers = Array.from(document.querySelectorAll<HTMLElement>(".overflow-y-auto"));
      let pageIsScrollable = false;
      for (const el of containers) {
        if (el.scrollHeight > el.clientHeight + 50) {
          pageIsScrollable = true;
          break;
        }
      }
      if (pageIsScrollable && maxScrollPercent === 0) {
        posthog.capture("page_no_scroll_detected", {
          page: window.location.pathname,
          is_mobile: window.innerWidth < 768,
          device_width: window.innerWidth,
          scroll_height: document.documentElement.scrollHeight,
          client_height: window.innerHeight,
        });
      }
    }, 3000); // wait 3s after navigation
  };
}

/**
 * Identify user in PostHog — enriched with role, branch name, unit name, position.
 * Called on login so every PostHog event is associated with the correct person and org unit.
 */
export function identifyUser(userId: string, properties?: {
  username?: string;
  role?: string;
  branchId?: number | null;
  branchName?: string | null;   // e.g. ສາຂາວັງວຽງ
  unitName?: string | null;     // e.g. ຂະແໜງສິນເຊື່ອ
  position?: string | null;
}) {
  if (!POSTHOG_KEY) return;
  posthog.identify(userId, {
    username: properties?.username,
    role: properties?.role,
    branch_id: properties?.branchId,
    branch_name: properties?.branchName,
    unit_name: properties?.unitName,
    position: properties?.position,
    // Tag device type for easier filtering in PostHog dashboard
    device_type: window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop",
  });
  // ─── Store user info in localStorage so EnhancedErrorBoundary can include it in crash reports
  if (properties?.username) {
    try {
      localStorage.setItem("crs_user", JSON.stringify({
        username: properties.username,
        role: properties.role,
        branchName: properties.branchName,
        unitName: properties.unitName,
        position: properties.position,
      }));
    } catch {}
  }
}

export function resetUser() {
  if (!POSTHOG_KEY) return;
  posthog.reset();
}

// ─── Action Breadcrumb Buffer ─────────────────────────────────────────────────
// Keeps the last 10 user actions in memory so we can attach them to crash reports.
// This answers "what did the user do before the error?"
const ACTION_BUFFER_SIZE = 10;
const recentActions: Array<{ event: string; page: string; ts: number; props?: Record<string, any> }> = [];

/**
 * Track a user action event in PostHog AND store it in the local breadcrumb buffer.
 * Call this for all important user actions (button clicks, form steps, uploads, etc.)
 */
export function trackEvent(event: string, properties?: Record<string, any>) {
  if (!POSTHOG_KEY) return;
  // Store in breadcrumb buffer
  recentActions.push({
    event,
    page: window.location.pathname,
    ts: Date.now(),
    props: properties,
  });
  if (recentActions.length > ACTION_BUFFER_SIZE) recentActions.shift();
  posthog.capture(event, properties);
}

/**
 * Get the last N user actions as a readable string for crash reports.
 * Format: "[12:34] /add-customer → loan_form_save_draft"
 */
export function getRecentActions(limit = 10): string {
  const slice = recentActions.slice(-limit);
  if (slice.length === 0) return "(ບໍ່ມີ action ກ່ອນ error)";
  return slice
    .map((a) => {
      const time = new Date(a.ts).toLocaleTimeString("en-GB", { timeZone: "Asia/Vientiane", hour: "2-digit", minute: "2-digit", second: "2-digit" });
      const propsStr = a.props ? ` ${JSON.stringify(a.props).substring(0, 60)}` : "";
      return `[${time}] ${a.page} → ${a.event}${propsStr}`;
    })
    .join("\n");
}

/**
 * Get the PostHog session URL for the current session.
 * This links directly to the session replay in PostHog.
 */
export function getPostHogSessionUrl(): string | null {
  if (!POSTHOG_KEY) return null;
  try {
    const sessionId = posthog.get_session_id?.();
    if (!sessionId) return null;
    return `https://us.posthog.com/project/466822/replay/${sessionId}`;
  } catch {
    return null;
  }
}

export default posthog;
